import { useContext, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowRight, Building2, Home, Users, Award } from "lucide-react";
import { AppContext } from "@/App";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SearchBar from "@/components/SearchBar";
import PropertyCard from "@/components/PropertyCard";
import LeadCaptureForm from "@/components/LeadCaptureForm";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import SEO, { SEO_DATA, REAL_ESTATE_AGENT_SCHEMA } from "@/components/SEO";

const HERO_IMAGES = [
  "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1601701119533-fde20cecbf4e?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1635687202393-b7591bdaaafa?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1618946506952-a93a84299d38?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1729719022559-34978ede47d1?crop=entropy&cs=srgb&fm=jpg&q=85"
];

const HomePage = () => {
  const { t } = useTranslation();
  const { properties, loading } = useContext(AppContext);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const featuredProperties = properties.filter((p) => p.is_featured).slice(0, 6);
  const latestProperties = properties.slice(0, 6);

  const stats = [
    { number: "350+", labelKey: "stats.propertiesSold", icon: Home },
    { number: "50+", labelKey: "stats.propertiesPortfolio", icon: Building2 },
    { number: "200+", labelKey: "stats.happyClients", icon: Users },
    { number: "10+", labelKey: "stats.yearsExperience", icon: Award }
  ];

  const services = [
    {
      titleKey: "services.buy.title",
      descKey: "services.buy.description",
      link: "/comprar",
      linkKey: "services.buy.link"
    },
    {
      titleKey: "services.sell.title",
      descKey: "services.sell.description",
      link: "/vender",
      linkKey: "services.sell.link"
    },
    {
      titleKey: "services.rent.title",
      descKey: "services.rent.description",
      link: "/propiedades?transaction_type=Alquiler",
      linkKey: "services.rent.link"
    }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEO 
        title={SEO_DATA.home.title}
        description={SEO_DATA.home.description}
        keywords={SEO_DATA.home.keywords}
        url="/"
        schema={REAL_ESTATE_AGENT_SCHEMA}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div className="hero-bg">
          {HERO_IMAGES.map((img, index) => (
            <img
              key={index}
              src={img}
              alt={`Property ${index + 1}`}
              className={`w-full h-full object-cover absolute inset-0 transition-opacity duration-1000 ${
                index === currentImageIndex ? "opacity-100" : "opacity-0"
              }`}
            />
          ))}
          <div className="hero-overlay absolute inset-0 z-10" />
          
          <div className="absolute bottom-32 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {HERO_IMAGES.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentImageIndex
                    ? "bg-[#D4AF37] w-8"
                    : "bg-white/40 hover:bg-white/60"
                }`}
                aria-label={`Image ${index + 1}`}
              />
            ))}
          </div>
        </div>

        <div className="container-custom relative z-10 px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mb-8 md:mb-12"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white leading-tight">
              {t("hero.title")}
              <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl mt-1 text-[#D4AF37]">
                {t("hero.titleHighlight")}
              </span>
            </h1>
            <p className="max-w-xl text-sm sm:text-base md:text-lg mt-4 md:mt-6 text-white/70 leading-relaxed">
              {t("hero.subtitle")}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <SearchBar />
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-10 sm:py-12 md:py-16 bg-[#0a0a0a] border-y border-white/5">
        <div className="container-custom px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.labelKey}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <stat.icon className="w-6 h-6 sm:w-8 sm:h-8 mx-auto text-[#D4AF37] mb-2 sm:mb-4" />
                <p className="text-2xl sm:text-3xl md:text-4xl font-serif text-[#D4AF37]">{stat.number}</p>
                <p className="text-xs sm:text-sm text-[#A3A3A3] uppercase tracking-wider mt-1">{t(stat.labelKey)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Capture Form */}
      <LeadCaptureForm />

      {/* Featured Properties */}
      <section className="py-12 sm:py-16 md:py-24 lg:py-32" data-testid="featured-section">
        <div className="container-custom px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 mb-8 sm:mb-12">
            <div>
              <motion.span
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="text-[#D4AF37] text-xs sm:text-sm uppercase tracking-widest"
              >
                {t("featured.label")}
              </motion.span>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mt-2 sm:mt-3 text-2xl sm:text-3xl md:text-4xl text-white"
              >
                {t("featured.title")}
              </motion.h2>
            </div>
            <Link to="/propiedades">
              <Button
                variant="outline"
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black text-sm sm:text-base"
                data-testid="view-all-featured-btn"
              >
                {t("featured.viewAll")}
                <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="space-y-4">
                  <Skeleton className="aspect-[4/3] bg-[#1E1E1E]" />
                  <Skeleton className="h-4 w-20 bg-[#1E1E1E]" />
                  <Skeleton className="h-6 w-3/4 bg-[#1E1E1E]" />
                  <Skeleton className="h-4 w-1/2 bg-[#1E1E1E]" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
              {(featuredProperties.length > 0 ? featuredProperties : latestProperties).map((property, index) => (
                <PropertyCard key={property.id} property={property} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Services Section */}
      <section className="section-spacing bg-[#0a0a0a]" data-testid="services-section">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-[#D4AF37] text-sm uppercase tracking-widest"
            >
              {t("services.label")}
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-3 text-3xl md:text-4xl text-white"
            >
              {t("services.title")}
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.titleKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="group p-8 bg-[#121212]/50 border border-white/5 hover:border-[#D4AF37]/30 transition-all duration-500 text-center"
              >
                <h3 className="text-xl mb-4 text-white">{t(service.titleKey)}</h3>
                <p className="text-base mb-6 text-[#A3A3A3]">{t(service.descKey)}</p>
                <Link
                  to={service.link}
                  className="inline-flex items-center justify-center text-[#D4AF37] text-sm uppercase tracking-wider group-hover:gap-3 gap-2 transition-all"
                >
                  {t(service.linkKey)}
                  <ArrowRight size={16} />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing" data-testid="cta-section">
        <div className="container-custom">
          <div className="relative overflow-hidden bg-[#121212] border border-white/5 p-12 md:p-20 text-center">
            <div className="relative z-10 max-w-3xl mx-auto">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl lg:text-5xl text-white mb-6"
              >
                {t("cta.title")}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-[#A3A3A3] text-lg mb-8"
              >
                {t("cta.subtitle")}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="flex flex-col sm:flex-row gap-4 justify-center"
              >
                <Link to="/contacto">
                  <Button
                    className="bg-[#D4AF37] text-black hover:bg-[#FFE082] px-8 py-6 uppercase tracking-wider"
                    data-testid="cta-contact-btn"
                    onClick={() => window.scrollTo(0, 0)}
                  >
                    {t("cta.contact")}
                  </Button>
                </Link>
                <a 
                  href={`https://wa.me/50242547204?text=${encodeURIComponent("¡Hola Ikonic Deals! 👋\n\nMe interesa conocer más sobre sus propiedades disponibles.\n\n¿Podrían ayudarme?")}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button
                    className="bg-[#25D366] text-white hover:bg-[#20BD5A] px-8 py-6 uppercase tracking-wider"
                    data-testid="cta-whatsapp-btn"
                  >
                    {t("cta.whatsapp")}
                  </Button>
                </a>
              </motion.div>
            </div>
            
            <div className="absolute -right-20 -bottom-20 w-80 h-80 bg-[#D4AF37]/5 rounded-full blur-3xl" />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
